import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomModal } from "../../../Everou/_components/CustomModal";
import { CapsuleButton } from "../../../Everou/_components";
import { IsoRangeDatePicker } from "../../../Everou/_components/IsoRangeDatePicker";
import { TimeHelper } from "../../../Everou/_helpers";
import { PMSLocationsService } from "../../_services/PMSLocationsService";
import { useGlobalNotification } from "../../../Common/_hooks/useGlobalNotification";
import { PMSReservation } from "../../_models/PMSIntegration";

export type EditModalMode = "edit" | "confirmDelete";

type Props = {
    reservation: PMSReservation;
    mode: EditModalMode;
    onClose: () => void;
    onChanged: () => void;
};

// Inline edit / delete for a reservation from the global listing. The update
// and delete services only need the reservation uid, so this works without a
// selected location. Dates are shown/edited in the viewer's timezone, matching
// how the global table renders them. On success we ask the caller to refetch.
export function EditGlobalReservationModal({
    reservation,
    mode,
    onClose,
    onChanged,
}: Props) {

    const { t } = useTranslation();
    const { notify } = useGlobalNotification();

    const timezone = TimeHelper.getCurrentTimezone();
    const [currentMode, setCurrentMode] = useState<EditModalMode>(mode);
    const [startDate, setStartDate] = useState<any>(
        TimeHelper.serverStringToIsoString(reservation.reservationStart),
    );
    const [endDate, setEndDate] = useState<any>(
        TimeHelper.serverStringToIsoString(reservation.reservationEnd),
    );
    const [busy, setBusy] = useState(false);

    const toIso = (value: any) =>
        value instanceof Date ? value.toISOString() : value;

    const handleSave = async () => {
        if (busy) return;
        setBusy(true);
        try {
            await PMSLocationsService.updateReservation(
                reservation.uid,
                toIso(startDate),
                toIso(endDate),
            );
            notify(t("mod_pms_reservation_updated"), "success");
            onChanged();
            onClose();
        } catch (e) {
            notify(t("global_error"), "error");
            setBusy(false);
        }
    };

    const handleDelete = async () => {
        if (busy) return;
        setBusy(true);
        try {
            await PMSLocationsService.deleteReservation(reservation.uid);
            notify(t("mod_pms_reservation_deleted"), "success");
            onChanged();
            onClose();
        } catch (e) {
            notify(t("global_error"), "error");
            setBusy(false);
        }
    };

    const titleKey =
        currentMode === "confirmDelete"
            ? "mod_pms_reservation_delete_question"
            : "mod_pms_reservation_edit_title";

    return (
        <CustomModal
            show={true}
            onHide={onClose}
            title={t(titleKey, { id: reservation.reservationId })}
            content={
                currentMode === "confirmDelete" ? deleteContent() : editContent()
            }
            footer={currentMode === "confirmDelete" ? deleteFooter() : editFooter()}
        />
    );

    function editContent() {
        return (
            <div>
                <div className="text-secondary mb-1">
                    {reservation.locationName}
                </div>
                <div className="text-secondary mb-3">{reservation.email}</div>
                <IsoRangeDatePicker
                    compact
                    onChangeStart={setStartDate}
                    onChangeEnd={setEndDate}
                    timezone={timezone}
                    begin={startDate}
                    end={endDate}
                    minDate={new Date()}
                    usePortal={true}
                />
            </div>
        );
    }

    function editFooter() {
        return (
            <div className="d-flex align-items-center justify-content-between w-100">
                <CapsuleButton
                    text={t("global_delete")}
                    style="DANGER"
                    fillStyle="OUTLINED"
                    isEnabled={!busy}
                    onClick={() => setCurrentMode("confirmDelete")}
                />
                <div className="d-flex align-items-center" style={{ columnGap: "8px" }}>
                    <CapsuleButton
                        text={t("global_cancel")}
                        style="INFO"
                        fillStyle="OUTLINED"
                        isEnabled={!busy}
                        onClick={onClose}
                    />
                    <CapsuleButton
                        text={t("global_save")}
                        isEnabled={!busy}
                        onClick={handleSave}
                    />
                </div>
            </div>
        );
    }

    function deleteContent() {
        return (
            <div className="text-secondary">
                {t("mod_pms_reservation_delete_description")}
            </div>
        );
    }

    function deleteFooter() {
        return (
            <div className="d-flex align-items-center justify-content-end w-100" style={{ columnGap: "8px" }}>
                <CapsuleButton
                    text={t("global_cancel")}
                    style="INFO"
                    fillStyle="OUTLINED"
                    isEnabled={!busy}
                    onClick={mode === "edit" ? () => setCurrentMode("edit") : onClose}
                />
                <CapsuleButton
                    text={t("global_delete")}
                    style="DANGER"
                    isEnabled={!busy}
                    onClick={handleDelete}
                />
            </div>
        );
    }
}
